// builders/SubGroupPlacementBuilder.js
import { v4 as uuidv4 } from "uuid";
import { sectionMatchNames } from "./sectionMatch";

const lc = (v) => (v || "").trim().toLowerCase();

/**
 * Build all SubGroupPlacements for all SectionPlacements.
 *
 * A SubGroup belongs to a section when its name is one of the section's
 * match names (catalog mapping / custom selection / own name - see sectionMatch.js).
 *
 * @param {Object} scenario
 * @param {Array} sectionPlacements
 * @param {Array} subGroups
 */
export function buildSubGroupPlacements(scenario, sectionPlacements, subGroups) {
  const out = [];

  (scenario.sections ?? []).forEach((section) => {
    const sp = sectionPlacements.find(
      (p) => String(p.referenceId) === String(section.id)
    );
    if (!sp) return;

    // names this section matches
    const names = new Set(sectionMatchNames(section).map(lc));

    // active sequence
    const seq = section.subGroupSequences?.find(
      (x) => x.key === section.activeSubGroupSequenceKey
    );

    // order of types
    const orderedTypes = seq
      ? (seq.groupOrders ?? [])
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((g) => g.type)
      : [];

    // The order is defined by the user (saved sequence, "Device Placement
    // Sequence" dialog). Types that are not in it yet (e.g. a freshly seeded
    // scenario has an empty sequence) come after the saved ones, in the order
    // the SubGroups were delivered - no alphabetical sorting.
    const matching = subGroups.filter((sg) => names.has(lc(sg.name)));

    const unlistedTypes = [
      ...new Set(
        matching
          .map((sg) => sg.type ?? null)
          .filter((t) => t && !orderedTypes.includes(t))
      ),
    ];

    const orderOfType = (t) => {
      if (!t) return 9999;
      const i = orderedTypes.indexOf(t);
      if (i >= 0) return i;
      return orderedTypes.length + unlistedTypes.indexOf(t);
    };

    // create SubGroupPlacements for every SubGroup whose name matches
    matching.forEach((sg) => {
      const sgType = sg.type ?? null;

      out.push({
        // Placement fields
        id: uuidv4(),
        referenceId: sg.id,
        parentId: sp.id,
        position: { x: 0, y: 0, z: 0 },
        size: { width: 0, height: 0, depth: 0 },
        enclosureId: sp.parentId,
        // REQUIRED backend fields
        type: sgType,
        devicePlacements: [],

        // your ordering field (not part of Placement)
        order: orderOfType(sgType),
      });
    });
  });

  return out;
}